import React, { useEffect, Suspense, lazy } from 'react'
import BackToTopButton from './components/BackToTopButton'
import WhatsAppButton from './components/WhatsAppButton'
import FrontEndLayout from '../src/layouts/FrontEndLayout'
import BackEndLayout from '../src/layouts/BackendLayout'
import FullScreenLoader from './components/FullScreenLoader'
import ProtectedRoute from './layouts/ProtectedRoute.js'
import { useAuthRedirect, useAutoLogout } from './libs/auth/index.js'
import { Routes, Route, Navigate } from 'react-router-dom'
import './App.css'; // Your global styles


import '../node_modules/datatables.net/js/dataTables.min.js'
import '../node_modules/datatables.net-bs4/js/dataTables.bootstrap4.min.js'
import '../node_modules/datatables.net-buttons/js/buttons.html5.min.js'
import '../node_modules/datatables.net-buttons/js/buttons.print.min.js'
import '../node_modules/datatables.net-buttons/js/dataTables.buttons.min.js'
import '../node_modules/datatables.net-buttons/js/buttons.colVis.min.js'
import '../node_modules/datatables.net-buttons-bs4/js/buttons.bootstrap4.min.js'
import '../node_modules/datatables.net-responsive-bs4/js/responsive.bootstrap4.min.js'
import '../node_modules/datatables.net-dt/js/dataTables.dataTables.min.js'
import '../node_modules/datatables.net-responsive/js/dataTables.responsive.min.js'

//using lazy load
const WelcomePage = lazy(() => import('./views/frontend/WelcomePage'))
const UnauthorizedPage = lazy(() => import('./components/UnauthorizedPage'))
const ErrorPage = lazy(() => import('./components/ErrorPage'))

//Auth Pages
const LoginPage = lazy(() => import('./views/auth/LoginPage'))
const RegisterPage = lazy(() => import('./views/auth/RegisterPage'))
const ForgotPassword = lazy(() => import('./views/auth/ForgotPassword'))
const VerifyEmail = lazy(() => import('./views/auth/VerifyEmail'))

//FrontEnd Pages
const MyAccount = lazy(() => import('./views/frontend/account/MyAccount'))
const AboutUs = lazy(() => import('./views/frontend/about/AboutUs'))
const ContactUs = lazy(() => import('./views/frontend/contact/ContactUs'))
const Faq = lazy(() => import('./views/frontend/faq/Faq'))
const Services = lazy(() => import('./views/frontend/service/Services'))
const CheckOut = lazy(() => import('./views/frontend/checkout/CheckOut.js'))
const Centers = lazy(() => import('./views/frontend/center/Centers.js'))
const MySupportTickets = lazy(() => import('./views/frontend/support/MySupportTickets'))
const CreateSupportTicket = lazy(() => import('./views/frontend/support/CreateSupportTicket'))
const ViewSupportTicket = lazy(() => import('./views/frontend/support/ViewSupportTicket'))
const MyEnrollments = lazy(() => import('./views/frontend/enrollment/MyEnrollments'))
const TrackEnrollment = lazy(() => import('./views/frontend/enrollment/TrackEnrollment'))

//BackEnd Pages
const Dashboard = lazy(() => import('./views/backend/Dashboard'))
const AllServices = lazy(() => import('./views/backend/service/superadmin/AllServices.js'))
const AddService = lazy(() => import('./views/backend/service/superadmin/AddService.js'))
const EditService = lazy(() => import('./views/backend/service/superadmin/EditService.js'))
const AllCenters = lazy(() => import('./views/backend/center/superadmin/AllCenters.js'))
const EditCenter = lazy(() => import('./views/backend/center/superadmin/EditCenter.js'))
const AddCenter = lazy(() => import('./views/backend/center/superadmin/AddCenter.js'))
const AllSupportTickets = lazy(() => import('./views/backend/support/superadmin/AllSupportTickets.js'))
const EditSupportTicket = lazy(() => import('./views/backend/support/superadmin/EditSupportTicket.js'))
const AllContactMessages = lazy(() => import('./views/backend/contact/superadmin/AllContactMessages.js'))
const ReplyContactMessage = lazy(() => import('./views/backend/contact/superadmin/ReplyContactMessage.js'))
const AllNewsletterSubscribers = lazy(() => import('./views/backend/newsletter/superadmin/AllNewsletterSubscribers.js'));
const AllSessions = lazy(() => import('./views/backend/session/superadmin/AllSessions.js'));
const AllUsers = lazy(() => import('./views/backend/user/superadmin/AllUsers.js'));
const ViewUser = lazy(() => import('./views/backend/user/superadmin/ViewUser.js'));
const ViewUserAdmin = lazy(() => import('./views/backend/user/admin/ViewUser.js'));
const AllAdmins = lazy(() => import('./views/backend/user/superadmin/RoleBasedUserPages.js').then(module => ({ default: module.AllAdmins })));
const AllManagers = lazy(() => import('./views/backend/user/superadmin/RoleBasedUserPages.js').then(module => ({ default: module.AllManagers })));
const AllModerators = lazy(() => import('./views/backend/user/superadmin/RoleBasedUserPages.js').then(module => ({ default: module.AllModerators })));
const AllAgents = lazy(() => import('./views/backend/user/superadmin/RoleBasedUserPages.js').then(module => ({ default: module.AllAgents })));
const AllPartners = lazy(() => import('./views/backend/user/superadmin/RoleBasedUserPages.js').then(module => ({ default: module.AllPartners })));
const AllAuditors = lazy(() => import('./views/backend/user/superadmin/RoleBasedUserPages.js').then(module => ({ default: module.AllAuditors })));
const AllSupport = lazy(() => import('./views/backend/user/superadmin/RoleBasedUserPages.js').then(module => ({ default: module.AllSupport })));
const AdminAllUsers = lazy(() => import('./views/backend/user/admin/AdminAllUsers.js'));
const AdminAllCenters = lazy(() => import('./views/backend/center/admin/AllCenters.js'));
const AdminAddCenter = lazy(() => import('./views/backend/center/admin/AddCenter.js'));
const AdminEditCenter = lazy(() => import('./views/backend/center/admin/EditCenter.js'));
const AdminViewCenter = lazy(() => import('./views/backend/center/admin/ViewCenter.js'));
const AdminAllEnrollments = lazy(() => import('./views/backend/enrollment/admin/AllEnrollments.js'));
const AllEnrollments = lazy(() => import('./views/backend/enrollment/superadmin/AllEnrollments.js'));
const AdminViewEnrollment = lazy(() => import('./views/backend/enrollment/ViewEnrollment.js'));
const AdminEditEnrollment = lazy(() => import('./views/backend/enrollment/admin/EditEnrollment.js'));
const SuperAdminAllPayments = lazy(() => import('./views/backend/payment/superadmin/AllPayments.js'));
const AdminAllPayments = lazy(() => import('./views/backend/payment/admin/AllPayments.js'));

// Manager backend pages
const ManagerAllCenters = lazy(() => import('./views/backend/center/manager/AllCenters.js'));
const ManagerViewCenter = lazy(() => import('./views/backend/center/manager/ViewCenter.js'));
const ManagerEditCenter = lazy(() => import('./views/backend/center/manager/EditCenter.js'));
const ManagerTeamList = lazy(() => import('./views/backend/user/manager/TeamList.js'));
const ManagerViewTeamMember = lazy(() => import('./views/backend/user/manager/ViewTeamMember.js'));
const ManagerAllEnrollments = lazy(() => import('./views/backend/enrollment/manager/AllEnrollments.js'));
const ManagerViewEnrollment = lazy(() => import('./views/backend/enrollment/manager/ViewEnrollment.js'));
const ManagerEditEnrollment = lazy(() => import('./views/backend/enrollment/manager/EditEnrollment.js'));

// Moderator backend pages
const ModeratorAllEnrollments = lazy(() => import('./views/backend/enrollment/moderator/AllEnrollments.js'));
const ModeratorViewEnrollment = lazy(() => import('./views/backend/enrollment/moderator/ViewEnrollment.js'));
const ModeratorEditEnrollment = lazy(() => import('./views/backend/enrollment/moderator/EditEnrollment.js'));
const ModeratorAgentList = lazy(() => import('./views/backend/user/moderator/AgentList.js'));
const ModeratorViewAgent = lazy(() => import('./views/backend/user/moderator/ViewAgent.js'));

// Auditor backend pages
const AuditorAllPayments = lazy(() => import('./views/backend/payment/auditor/AllPayments.js'));
const AuditorViewPayment = lazy(() => import('./views/backend/payment/auditor/ViewPayment.js'));
const AuditorAllEnrollments = lazy(() => import('./views/backend/enrollment/auditor/AllEnrollments.js'));
const AuditorViewEnrollment = lazy(() => import('./views/backend/enrollment/auditor/ViewEnrollment.js'));
const AuditorAllCenters = lazy(() => import('./views/backend/center/auditor/AllCenters.js'));
const AuditorViewCenter = lazy(() => import('./views/backend/center/auditor/ViewCenter.js'));

// Agent backend pages
const AgentAllEnrollments = lazy(() => import('./views/backend/enrollment/agent/AllEnrollments.js'));
const AgentViewEnrollment = lazy(() => import('./views/backend/enrollment/agent/ViewEnrollment.js'));
const AgentEditEnrollment = lazy(() => import('./views/backend/enrollment/agent/EditEnrollment.js'));

// Support backend pages
const SupportAllTickets = lazy(() => import('./views/backend/support/support/AllTickets.js'));
const SupportViewTicket = lazy(() => import('./views/backend/support/support/ViewTicket.js'));
const SupportEditTicket = lazy(() => import('./views/backend/support/support/EditTicket.js'));
const SupportAllContacts = lazy(() => import('./views/backend/contact/support/AllContacts.js'));
const SupportViewContact = lazy(() => import('./views/backend/contact/support/ViewContact.js'));
const SupportReplyContact = lazy(() => import('./views/backend/contact/support/ReplyContact.js'));

function App() {

  useAuthRedirect() // custom hook is responsible for checking the user's authentication status and role and handle redirection to home page from login (already logged in users) and registration (users with certain 'role' can't access the registration) pages.

  useAutoLogout() // A custom hook that checks the token's expiration time every second and logs out the user if the token is expired. It displays a SweetAlert notification and navigates the user to the login page.

  return (
    <div className='App'>
      <Suspense fallback={<FullScreenLoader />}>
        <Routes>
          {/* frontend */}
          <Route element={<FrontEndLayout />}>
            <Route path='/' element={<WelcomePage />} />
            <Route path='/login' element={<LoginPage />} />
            <Route path='/register' element={<RegisterPage />} />
            <Route path='/forgot/password' element={<ForgotPassword />} />
            <Route path='/verify/email' element={<VerifyEmail />} />
            <Route path='/about' element={<AboutUs />} />
            <Route path='/contact' element={<ContactUs />} />
            <Route path='/faq' element={<Faq />} />
            <Route path='/services' element={<Services />} />
            <Route path='/checkout' element={<CheckOut />} />
            <Route path='/centers' element={<Centers />} />
            <Route path='/track-enrollment' element={<TrackEnrollment />} />

            {/* protected routes frontend */}
            <Route
              element={
                <ProtectedRoute
                  allowedRoles={[
                    'SuperAdmin',
                    'Admin',
                    'Moderator',
                    'Agent',
                    'Partner',
                    'User',
                    'Auditor',
                    'Support',
                    'Manager'
                  ]}
                />
              }
            >
              <Route path='/account' element={<MyAccount />} />
              <Route path='/support/my-tickets' element={<MySupportTickets />} />
              <Route path='/support/create-ticket' element={<CreateSupportTicket />} />
              <Route path='/support/ticket/:ticketId' element={<ViewSupportTicket />} />
              <Route path='/enrollments' element={<MyEnrollments />} />
              <Route path='/enrollments/:enrollmentId' element={<TrackEnrollment />} />
            </Route>
          </Route>

          {/* General backend protected routes*/}
          <Route element={<BackEndLayout />}>
            <Route
              element={
                <ProtectedRoute
                  allowedRoles={[
                    'SuperAdmin',
                    'Admin',
                    'Moderator',
                    'Agent',
                    'Partner',
                    'Auditor',
                    'Support',
                    'Manager'
                  ]}
                />
              }
            >
              <Route path='/dashboard' element={<Dashboard />} />
            </Route>
          </Route>

          {/* Agent backend protected routes*/}
          <Route element={<BackEndLayout />}>
            <Route element={<ProtectedRoute allowedRoles={['Agent']} />}>
              {/* Enrollment Management */}
              <Route path='/agent/enrollments' element={<AgentAllEnrollments />} />
              <Route path='/agent/enrollments/view/:enrollmentId' element={<AgentViewEnrollment />} />
              <Route path='/agent/enrollments/edit/:enrollmentId' element={<AgentEditEnrollment />} />
            </Route>
          </Route>

          {/* Auditor backend protected routes*/}
          <Route element={<BackEndLayout />}>
            <Route element={<ProtectedRoute allowedRoles={['Auditor']} />}>
              {/* Payment Audit */}
              <Route path='/audit/payments' element={<AuditorAllPayments />} />
              <Route path='/audit/payments/view/:paymentId' element={<AuditorViewPayment />} />
              {/* Enrollment Audit */}
              <Route path='/audit/enrollments' element={<AuditorAllEnrollments />} />
              <Route path='/audit/enrollments/view/:enrollmentId' element={<AuditorViewEnrollment />} />
              {/* Center Audit */}
              <Route path='/audit/centers' element={<AuditorAllCenters />} />
              <Route path='/audit/centers/view/:centerId' element={<AuditorViewCenter />} />
            </Route>
          </Route>

          {/* Support backend protected routes*/}
          <Route element={<BackEndLayout />}>
            <Route element={<ProtectedRoute allowedRoles={['Support']} />}>
              {/* Support Ticket Management */}
              <Route path='/support/tickets' element={<SupportAllTickets />} />
              <Route path='/support/tickets/view/:ticketId' element={<SupportViewTicket />} />
              <Route path='/support/tickets/edit/:ticketId' element={<SupportEditTicket />} />
              {/* Support Contact Management */}
              <Route path='/support/contact' element={<SupportAllContacts />} />
              <Route path='/support/contact/view/:contactId' element={<SupportViewContact />} />
              <Route path='/support/contact/reply/:contactId' element={<SupportReplyContact />} />
            </Route>
          </Route>

          {/* Manager backend protected routes*/}
          <Route element={<BackEndLayout />}>
            <Route element={<ProtectedRoute allowedRoles={['Manager']} />}>
              {/* Center Management */}
              <Route path='/manager/centers' element={<ManagerAllCenters />} />
              <Route path='/manager/centers/view/:centerId' element={<ManagerViewCenter />} />
              <Route path='/manager/centers/edit/:centerId' element={<ManagerEditCenter />} />
              {/* Team Management */}
              <Route path='/manager/team' element={<ManagerTeamList />} />
              <Route path='/manager/team/view/:userId' element={<ManagerViewTeamMember />} />
              {/* Enrollment Management */}
              <Route path='/manager/enrollments' element={<ManagerAllEnrollments />} />
              <Route path='/manager/enrollments/view/:enrollmentId' element={<ManagerViewEnrollment />} />
              <Route path='/manager/enrollments/edit/:enrollmentId' element={<ManagerEditEnrollment />} />
            </Route>
          </Route>

          {/* Partner backend protected routes*/}
          <Route element={<BackEndLayout />}>
            <Route element={<ProtectedRoute allowedRoles={['Partner']} />}>
            </Route>
          </Route>

          {/* Moderator backend protected routes*/}
          <Route element={<BackEndLayout />}>
            <Route element={<ProtectedRoute allowedRoles={['Moderator']} />}>
              {/* Enrollment Management */}
              <Route path='/moderator/enrollments' element={<ModeratorAllEnrollments />} />
              <Route path='/moderator/enrollments/view/:enrollmentId' element={<ModeratorViewEnrollment />} />
              <Route path='/moderator/enrollments/edit/:enrollmentId' element={<ModeratorEditEnrollment />} />
              {/* Agent Management */}
              <Route path='/moderator/agents' element={<ModeratorAgentList />} />
              <Route path='/moderator/agents/view/:userId' element={<ModeratorViewAgent />} />
            </Route>
          </Route>

          {/* Admin backend protected routes*/}
          <Route element={<BackEndLayout />}>
            <Route
              element={
                <ProtectedRoute
                  allowedRoles={['Admin']}
                />
              }
            >
              {/* Admin User Management Routes */}
              <Route path='/admin/users' element={<AdminAllUsers />} />
              <Route path='/admin/users/view/:userId' element={<ViewUserAdmin />} />

              {/* Admin Center Management Routes */}
              <Route path='/admin/centers' element={<AdminAllCenters />} />
              <Route path='/admin/centers/add' element={<AdminAddCenter />} />
              <Route path='/admin/centers/edit/:centerId' element={<AdminEditCenter />} />
              <Route path='/admin/centers/view/:centerId' element={<AdminViewCenter />} />

              {/* Admin Enrollment Management Routes */}
              <Route path='/admin/enrollments' element={<AdminAllEnrollments />} />
              <Route path='/admin/payments' element={<AdminAllPayments />} />

            </Route>
          </Route>

          {/* SuperAdmin backend protected routes*/}
          <Route element={<BackEndLayout />}>
            <Route
              element={
                <ProtectedRoute
                  allowedRoles={['SuperAdmin']}
                />
              }
            >
              <Route path='/system/services' element={<AllServices />} />
              <Route path='/system/add-service' element={<AddService />} />
              <Route path='/system/edit-service/:serviceId' element={<EditService />} />
              <Route path='/centers/all' element={<AllCenters />} />
              <Route path='/centers/add' element={<AddCenter />} />
              <Route path='/centers/edit/:centerId' element={<EditCenter />} />
              <Route path='/system/newsletters' element={<AllNewsletterSubscribers />} />
              <Route path='/system/sessions' element={<AllSessions />} />
              <Route path='/system/payments' element={<SuperAdminAllPayments />} />

              {/* User Management Routes */}
              <Route path='/users/all' element={<AllUsers />} />
              <Route path='/users/view/:userId' element={<ViewUser />} />
              <Route path='/users/admins' element={<AllAdmins />} />
              <Route path='/users/managers' element={<AllManagers />} />
              <Route path='/users/moderators' element={<AllModerators />} />
              <Route path='/users/agents' element={<AllAgents />} />
              <Route path='/users/partners' element={<AllPartners />} />
              <Route path='/users/auditors' element={<AllAuditors />} />
              <Route path='/users/support' element={<AllSupport />} />

              {/* Admin Enrollment Management Routes */}
              <Route path='/enrollments/all' element={<AllEnrollments />} />

            </Route>
          </Route>

          {/* SuperAdmin & Admin backend protected routes*/}
          <Route element={<BackEndLayout />}>
            <Route
              element={
                <ProtectedRoute
                  allowedRoles={['SuperAdmin', 'Admin']}
                />
              }
            >

              <Route path='/admin/enrollments/view/:enrollmentId' element={<AdminViewEnrollment />} />
            </Route>
          </Route>

          {/* SuperAdmin, Admin, Moderator, Agent backend protected routes*/}
          <Route element={<BackEndLayout />}>
            <Route
              element={
                <ProtectedRoute
                  allowedRoles={['SuperAdmin', 'Admin', 'Moderator', 'Agent']}
                />
              }
            >

              <Route path='/admin/enrollments/edit/:enrollmentId' element={<AdminEditEnrollment />} />

              <Route path='/admin/enrollments/view/:enrollmentId' element={<AdminViewEnrollment />} />
            </Route>
          </Route>

          {/* Moderator & Agent backend protected routes*/}
          <Route element={<BackEndLayout />}>
            <Route
              element={
                <ProtectedRoute
                  allowedRoles={['Moderator', 'Agent']}
                />
              }
            >

            </Route>
          </Route>

          {/* 404 page */}
          <Route path='/404' element={<ErrorPage />} />

          {/* unauthorized */}
          <Route element={<FrontEndLayout />}>
            <Route path='/unauthorized' element={<UnauthorizedPage />} />
          </Route>

          {/* Catch-all route for unmatched paths, you can either use a 404 page or so */}
          <Route path='*' element={<Navigate to='/404' replace />} />
        </Routes>
        <BackToTopButton />
        <WhatsAppButton />
      </Suspense>
    </div>
  );
}

export default App;
